import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Calendar, Award, Clock, UserCheck, X, Bell, Trash2, RefreshCw, QrCode, Settings, Mail, Users } from 'lucide-react';
import EventQRPrintout from '@/components/EventQRPrintout';
import { formatDateDisplay, formatDateForInput } from '@/utils/dateUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BusinessDashboard() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [filter, setFilter] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [businessJoinRequests, setBusinessJoinRequests] = useState([]);
  const [eventJoinRequests, setEventJoinRequests] = useState([]);
  const [businessSubscribers, setBusinessSubscribers] = useState([]);
  const [totalJoinee, setTotalJoinee] = useState(0);
  const [businessApproved, setBusinessApproved] = useState(false);
  const [businessInfo, setBusinessInfo] = useState(null);
  const [businessSettings, setBusinessSettings] = useState({
    auto_approve_customers: false,
    hide_coupon_value: true,
    is_premium: false
  });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showQRPrintout, setShowQRPrintout] = useState(false);
  const [selectedEventForQR, setSelectedEventForQR] = useState(null);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [eventForm, setEventForm] = useState({
    event_name: '',
    event_description: '',
    coupon_value: '',
    start_date: '',
    end_date: '',
    prizes: [
      { prize_name: '', prize_amount: '' },
      { prize_name: '', prize_amount: '' },
      { prize_name: '', prize_amount: '' }
    ]
  });

  useEffect(() => {
    fetchBusinessInfo();
    fetchEvents();
    fetchJoinRequests();
    fetchBusinessSettings();
  }, []);

  useEffect(() => {
    applyFilter();
  }, [events, filter]);

  const fetchBusinessInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBusinessInfo(response.data);
      setBusinessApproved(response.data.approval_status === 'approved');
    } catch (error) {
      console.error('Error fetching business info:', error);
      if (error.response?.status === 401) {
        navigate('/login');
      }
    }
  };

  const fetchBusinessSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/business/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBusinessSettings(response.data);
    } catch (error) {
      console.error('Error fetching business settings:', error);
    }
  };

  const updateBusinessSettings = async (settingName, value) => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      params.append(settingName, value);
      
      await axios.put(`${API}/business/settings?${params.toString()}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local state
      setBusinessSettings(prev => ({ ...prev, [settingName]: value }));
      toast.success('Settings updated successfully');
      
      // If enabling auto-approve, refresh join requests
      if (settingName === 'auto_approve_customers' && value) {
        fetchJoinRequests();
      }
    } catch (error) {
      console.error('Error updating settings:', error);
      toast.error('Failed to update settings');
    }
  };


  const applyFilter = () => {
    if (filter === 'all') {
      setFilteredEvents(events);
    } else if (filter === 'active') {
      setFilteredEvents(events.filter(e => e.status === 'active'));
    } else if (filter === 'pending') {
      setFilteredEvents(events.filter(e => e.approval_status === 'pending'));
    } else if (filter === 'completed') {
      setFilteredEvents(events.filter(e => e.status === 'completed'));
    } else if (filter === 'draft') {
      setFilteredEvents(events.filter(e => e.status === 'draft'));
    }
  };

  const fetchEvents = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/events/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const fetchJoinRequests = async () => {
    try {
      const token = localStorage.getItem('token');
      const [businessRes, eventRes, subscribersRes] = await Promise.all([
        axios.get(`${API}/businesses/join-requests`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/events/join-requests`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/businesses/subscribers`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setBusinessJoinRequests(businessRes.data);
      setEventJoinRequests(eventRes.data);
      setBusinessSubscribers(subscribersRes.data);
      setTotalJoinee(subscribersRes.data.length);
    } catch (error) {
      console.error('Error fetching join requests:', error);
    }
  };

  const handleApproveBusinessJoin = async (membershipId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/businesses/approve-join/${membershipId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Business join request approved!');
      fetchJoinRequests();
    } catch (error) {
      toast.error('Failed to approve request');
    }
  };

  const handleRejectBusinessJoin = async (membershipId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/businesses/reject-join/${membershipId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Business join request rejected');
      fetchJoinRequests();
    } catch (error) {
      toast.error('Failed to reject request');
    }
  };

  const handleApproveEventJoin = async (membershipId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/events/approve-join/${membershipId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Event join request approved!');
      fetchJoinRequests();
    } catch (error) {
      toast.error('Failed to approve request');
    }
  };

  const handleRejectEventJoin = async (membershipId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/events/reject-join/${membershipId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Event join request rejected');
      fetchJoinRequests();
    } catch (error) {
      toast.error('Failed to reject request');
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        fetchBusinessInfo(),
        fetchEvents(),
        fetchJoinRequests()
      ]);
      toast.success('Dashboard refreshed successfully!');
    } catch (error) {
      toast.error('Failed to refresh dashboard');
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/events`, eventForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Event created successfully!');
      setShowCreateDialog(false);
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create event');
    }
  };

  const handleActivateEvent = async (eventId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/events/${eventId}/status?status=active`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Event activated!');
      fetchEvents();
    } catch (error) {
      toast.error('Failed to activate event');
    }
  };

  const handleEditEvent = (event) => {
    setEditingEvent(event);
    setEventForm({
      event_name: event.event_name,
      event_description: event.event_description,
      coupon_value: event.coupon_value,
      start_date: formatDateForInput(event.start_date),
      end_date: formatDateForInput(event.end_date),
      draw_date: formatDateForInput(event.draw_date),
      prizes: event.prizes || []
    });
    setShowEditDialog(true);
  };

  const handleUpdateEvent = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      // For active events, send name, description, prizes, and dates
      // For draft events, send all fields
      const updateData = editingEvent.status === 'active' 
        ? { 
            event_name: eventForm.event_name,
            event_description: eventForm.event_description,
            start_date: eventForm.start_date,
            end_date: eventForm.end_date,
            draw_date: eventForm.draw_date,
            prizes: eventForm.prizes
          }
        : eventForm;
      
      await axios.patch(`${API}/events/${editingEvent.event_id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Event updated successfully!');
      setShowEditDialog(false);
      setEditingEvent(null);
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update event');
    }
  };

  const addPrizeRow = () => {
    setEventForm({
      ...eventForm,
      prizes: [...eventForm.prizes, { prize_name: '', prize_amount: '' }]
    });
  };

  const handleDeleteReceiptFiles = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete all receipt files for this event? This action cannot be undone.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API}/business/event/${eventId}/receipts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Successfully deleted ${response.data.deleted_count} receipt files`);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Receipt deletion not enabled for your business. Contact admin.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to delete receipt files');
      }
    }
  };

  const removePrizeRow = (index) => {
    if (index === 0) return; // Cannot remove first prize
    const newPrizes = eventForm.prizes.filter((_, i) => i !== index);
    setEventForm({ ...eventForm, prizes: newPrizes });
  };

  const handleShowQRPrintout = (event) => {
    setSelectedEventForQR(event);
    setShowQRPrintout(true);
  };

  const handleCloseQRPrintout = () => {
    setShowQRPrintout(false);
    setSelectedEventForQR(null);
  };

  const updatePrizeField = (index, field, value) => {
    const newPrizes = [...eventForm.prizes];
    newPrizes[index][field] = value;
    setEventForm({ ...eventForm, prizes: newPrizes });
  };

  const isFormValid = () => {
    return (
      eventForm.event_name.trim() !== '' &&
      eventForm.event_description.trim() !== '' &&
      eventForm.coupon_value !== '' &&
      eventForm.start_date !== '' &&
      eventForm.end_date !== '' &&
      eventForm.prizes.length > 0 &&
      eventForm.prizes[0].prize_name.trim() !== '' &&
      eventForm.prizes[0].prize_amount.trim() !== ''
    );
  };

  const activeEvents = (events || []).filter(e => e.status === 'active').length;
  const pendingEvents = (events || []).filter(e => e.approval_status === 'pending').length;
  const completedEvents = (events || []).filter(e => e.status === 'completed').length;

  return (
    <div className="min-h-screen bg-cream pt-4 sm:pt-6 pb-12 px-4 sm:px-0" data-testid="business-dashboard">
      <div className="max-w-7xl mx-auto sm:px-4 lg:px-8">
        {/* Header - Responsive */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6 sm:mb-8">
          <div>
            <h1 className="font-playfair font-bold text-3xl sm:text-4xl text-primary mb-2">Business Dashboard</h1>
            <p className="font-outfit text-sm sm:text-base text-gray-600">Manage your draw events</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={handleRefresh}
              variant="outline"
              className="text-sm"
              size="sm"
              disabled={isRefreshing}
              data-testid="refresh-button"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
              {isRefreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
            <Button
              onClick={() => navigate('/business/receipts')}
              className="bg-amber-600 hover:bg-amber-700 text-sm"
              size="sm"
              data-testid="review-receipts-button"
            >
              <Clock className="w-4 h-4 mr-2" />
              Review Receipts
            </Button>
            {businessInfo?.show_email_link && (
              <>
                <Button
                  onClick={() => navigate('/business/subscriber-pools')}
                  variant="outline"
                  size="sm"
                  title="Manage Subscriber Pools"
                  className="text-sm"
                >
                  <Users className="w-4 h-4 mr-2" />
                  Subscriber Pools
                </Button>
                <Button
                  onClick={() => navigate('/business/promotional-email')}
                  className="bg-blue-600 hover:bg-blue-700 text-sm"
                  size="sm"
                  title="Send Promotional Email"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Send Email
                </Button>
              </>
            )}
            <Button
              onClick={() => setShowSettingsDialog(true)}
              variant="outline"
              className="text-sm"
              size="sm"
              title="Business Settings"
            >
              <Settings className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Pending Requests Alert */}
        {((businessJoinRequests || []).length > 0 || (eventJoinRequests || []).length > 0) && (
          <Card className="mb-6 p-4 sm:p-6 border-2 border-red-500 bg-red-50 animate-pulse">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center">
                  <Bell className="w-6 h-6 text-white animate-bounce" />
                </div>
              </div>
              <div className="flex-1">
                <h3 className="font-playfair text-lg sm:text-xl font-bold text-red-900 mb-2">
                  ⚠️ Pending Requests Require Your Attention!
                </h3>
                <div className="space-y-1">
                  {(businessJoinRequests || []).length > 0 && (
                    <p className="font-outfit text-sm sm:text-base text-red-800">
                      <strong>{(businessJoinRequests || []).length}</strong> business join request{(businessJoinRequests || []).length > 1 ? 's' : ''} waiting for approval
                    </p>
                  )}
                  {(eventJoinRequests || []).length > 0 && (
                    <p className="font-outfit text-sm sm:text-base text-red-800">
                      <strong>{(eventJoinRequests || []).length}</strong> event join request{(eventJoinRequests || []).length > 1 ? 's' : ''} waiting for approval
                    </p>
                  )}
                </div>
                <p className="font-outfit text-xs sm:text-sm text-red-700 mt-3">
                  👉 Scroll down to the requests tabs to review and approve/reject them.
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Stats Cards - Responsive Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-8">
          <Card 
            className="p-4 sm:p-6 cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => setFilter('all')}
            data-testid="total-deals-card"
          >
            <p className="font-outfit text-xs sm:text-sm text-gray-600 mb-1">Total Events</p>
            <p className="font-playfair text-2xl sm:text-3xl font-bold text-primary" data-testid="total-events">{(events || []).length}</p>
          </Card>
          <Card 
            className="p-4 sm:p-6 cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => setFilter('active')}
            data-testid="active-deals-card"
          >
            <p className="font-outfit text-xs sm:text-sm text-gray-600 mb-1">Active Events</p>
            <p className="font-playfair text-2xl sm:text-3xl font-bold text-amber-600" data-testid="active-events">{activeEvents}</p>
          </Card>
          <Card 
            className="p-4 sm:p-6 cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => setFilter('pending')}
            data-testid="pending-deals-card"
          >
            <p className="font-outfit text-xs sm:text-sm text-gray-600 mb-1">Pending Approval</p>
            <p className="font-playfair text-2xl sm:text-3xl font-bold text-yellow-600" data-testid="pending-events">{pendingEvents}</p>
          </Card>
          <Card 
            className="p-4 sm:p-6 cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => setFilter('completed')}
            data-testid="completed-deals-card"
          >
            <p className="font-outfit text-xs sm:text-sm text-gray-600 mb-1">Completed</p>
            <p className="font-playfair text-2xl sm:text-3xl font-bold text-primary" data-testid="completed-events">{completedEvents}</p>
          </Card>
          <Card 
            className="p-4 sm:p-6 hover:shadow-lg transition-shadow"
            data-testid="total-joinee-card"
          >
            <p className="font-outfit text-xs sm:text-sm text-gray-600 mb-1">Total Joinee</p>
            <p className="font-playfair text-2xl sm:text-3xl font-bold text-green-600" data-testid="total-joinee">{totalJoinee}</p>
          </Card>
        </div>

        {/* Join Requests and Subscribers Tabs - Responsive */}
        <Tabs defaultValue="business-requests" className="w-full mb-6 sm:mb-8">
          <div className="overflow-x-auto mb-4 sm:mb-6">
            <TabsList className="inline-flex min-w-full sm:min-w-0">
              <TabsTrigger 
                value="business-requests" 
                className={`text-xs sm:text-sm whitespace-nowrap relative ${
                  (businessJoinRequests || []).length > 0 ? 'font-bold' : ''
                }`}
              >
                <div className="flex items-center gap-2">
                  <span>Business Requests</span>
                  {(businessJoinRequests || []).length > 0 ? (
                    <>
                      <Badge className="bg-red-500 text-white hover:bg-red-600 animate-pulse">
                        {(businessJoinRequests || []).length}
                      </Badge>
                      <Bell className="w-4 h-4 text-red-500 animate-bounce" />
                    </>
                  ) : (
                    <span className="text-gray-400">({(businessJoinRequests || []).length})</span>
                  )}
                </div>
              </TabsTrigger>
              <TabsTrigger 
                value="event-requests" 
                className={`text-xs sm:text-sm whitespace-nowrap relative ${
                  (eventJoinRequests || []).length > 0 ? 'font-bold' : ''
                }`}
              >
                <div className="flex items-center gap-2">
                  <span>Event Requests</span>
                  {(eventJoinRequests || []).length > 0 ? (
                    <>
                      <Badge className="bg-red-500 text-white hover:bg-red-600 animate-pulse">
                        {(eventJoinRequests || []).length}
                      </Badge>
                      <Bell className="w-4 h-4 text-red-500 animate-bounce" />
                    </>
                  ) : (
                    <span className="text-gray-400">({(eventJoinRequests || []).length})</span>
                  )}
                </div>
              </TabsTrigger>
              <TabsTrigger value="subscribers" className="text-xs sm:text-sm whitespace-nowrap">
                Subscribers ({totalJoinee})
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Business Join Requests Tab */}
          <TabsContent value="business-requests">
            {(businessJoinRequests || []).length === 0 ? (
              <Card className="p-8 sm:p-12 text-center">
                <p className="font-outfit text-sm sm:text-base text-gray-600">No pending business join requests</p>
              </Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {(businessJoinRequests || []).map((request) => (
                  <Card key={request.membership_id} className="p-4">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-outfit font-semibold text-base sm:text-lg break-words">{request.customer_name}</h3>
                        <p className="font-outfit text-xs sm:text-sm text-gray-600 break-all">{request.customer_email}</p>
                      </div>
                      <span className="text-xs font-mono bg-gray-100 px-2 py-1 rounded self-start">{request.customer_id}</span>
                    </div>
                    <p className="font-outfit text-xs text-gray-500 mb-3">
                      Requested: {new Date(request.requested_at).toLocaleDateString()}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleApproveBusinessJoin(request.membership_id)}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-xs sm:text-sm"
                        size="sm"
                      >
                        Approve
                      </Button>
                      <Button
                        onClick={() => handleRejectBusinessJoin(request.membership_id)}
                        variant="outline"
                        className="flex-1 text-xs sm:text-sm"
                        size="sm"
                      >
                        Reject
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Event Join Requests Tab */}
          <TabsContent value="event-requests">
            {(eventJoinRequests || []).length === 0 ? (
              <Card className="p-8 sm:p-12 text-center">
                <p className="font-outfit text-sm sm:text-base text-gray-600">No pending event join requests</p>
              </Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {(eventJoinRequests || []).map((request) => (
                  <Card key={request.membership_id} className="p-4">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-outfit font-semibold text-base sm:text-lg break-words">{request.customer_name}</h3>
                        <p className="font-outfit text-xs sm:text-sm text-gray-600 break-all">{request.customer_email}</p>
                      </div>
                      <span className="text-xs font-mono bg-gray-100 px-2 py-1 rounded self-start">{request.customer_id}</span>
                    </div>
                    <p className="font-outfit text-xs sm:text-sm text-amber-700 mb-1 break-words">
                      Event: {request.event_name}
                    </p>
                    <p className="font-outfit text-xs text-gray-500 mb-3">
                      Requested: {new Date(request.requested_at).toLocaleDateString()}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleApproveEventJoin(request.membership_id)}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-xs sm:text-sm"
                        size="sm"
                      >
                        Approve
                      </Button>
                      <Button
                        onClick={() => handleRejectEventJoin(request.membership_id)}
                        variant="outline"
                        className="flex-1"
                        size="sm"
                      >
                        Reject
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Business Subscribers Tab - Single Line Format */}
          <TabsContent value="subscribers">
            {(businessSubscribers || []).length === 0 ? (
              <Card className="p-8 sm:p-12 text-center">
                <p className="font-outfit text-sm sm:text-base text-gray-600">No business subscribers yet</p>
              </Card>
            ) : (
              <div className="space-y-2 sm:space-y-3">
                {(businessSubscribers || []).map((subscriber) => (
                  <Card key={subscriber.membership_id} className="p-3 sm:p-4 hover:shadow-md transition-shadow">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4 lg:gap-6 font-outfit text-xs sm:text-sm flex-1 min-w-0">
                        <span className="break-words"><strong>Customer:</strong> {subscriber.customer_name}</span>
                        <span className="break-all"><strong>Email:</strong> {subscriber.customer_email}</span>
                        <span className="break-all hidden sm:inline"><strong>Phone:</strong> {subscriber.customer_phone}</span>
                        <span className="hidden md:inline"><strong>Joined:</strong> {new Date(subscriber.approved_at).toLocaleDateString()}</span>
                      </div>
                      <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold self-start sm:self-auto">
                        Member
                      </span>
                    </div>
                    <div className="sm:hidden mt-2 text-xs text-gray-600">
                      <p><strong>Phone:</strong> {subscriber.customer_phone}</p>
                      <p><strong>Joined:</strong> {new Date(subscriber.approved_at).toLocaleDateString()}</p>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        <div className="mb-4 sm:mb-6">
          <h2 className="font-playfair font-bold text-xl sm:text-2xl text-primary mb-4">Your Events</h2>
        </div>

        <Tabs defaultValue="active" className="w-full">
          {/* Responsive layout: Stack on mobile, side-by-side on desktop */}
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4 sm:mb-6">
            {/* Tabs - Scrollable on mobile */}
            <div className="overflow-x-auto">
              <TabsList className="inline-flex min-w-full sm:min-w-0">
                <TabsTrigger value="active" className="text-xs sm:text-sm whitespace-nowrap">
                  Active ({(events || []).filter(e => e.status === 'active').length})
                </TabsTrigger>
                <TabsTrigger value="pending" className="text-xs sm:text-sm whitespace-nowrap">
                  Pending ({(events || []).filter(e => e.approval_status === 'pending').length})
                </TabsTrigger>
                <TabsTrigger value="completed" className="text-xs sm:text-sm whitespace-nowrap">
                  Completed ({(events || []).filter(e => e.status === 'completed').length})
                </TabsTrigger>
              </TabsList>
            </div>
            
            {/* Create Event Button - Full width on mobile */}
            {businessApproved ? (
              <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                <DialogTrigger asChild>
                  <Button 
                    className="bg-primary hover:bg-primary/90 w-full sm:w-auto text-sm" 
                    data-testid="create-event-button"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Event
                  </Button>
                </DialogTrigger>
            <DialogContent 
              className="max-w-xl max-h-[85vh] overflow-y-auto p-4 sm:p-6" 
              data-testid="create-event-dialog"
              onInteractOutside={(e) => e.preventDefault()}
            >
              <DialogHeader className="pb-3">
                <DialogTitle className="font-playfair text-xl sm:text-2xl">Create Draw Event</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateEvent} className="space-y-3 mt-2">
                <div>
                  <Label className="text-sm">Event Name</Label>
                  <Input
                    value={eventForm.event_name}
                    onChange={(e) => setEventForm({ ...eventForm, event_name: e.target.value })}
                    required
                    className="mt-1"
                    data-testid="event-name-input"
                  />
                </div>
                <div>
                  <Label className="text-sm">Description</Label>
                  <Textarea
                    value={eventForm.event_description}
                    onChange={(e) => setEventForm({ ...eventForm, event_description: e.target.value })}
                    required
                    rows={2}
                    className="mt-1"
                    data-testid="event-desc-input"
                  />
                </div>
                <div>
                  <Label className="text-sm">Incentive Points Value</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={eventForm.coupon_value}
                    onChange={(e) => setEventForm({ ...eventForm, coupon_value: e.target.value })}
                    required
                    className="mt-1"
                    data-testid="coupon-value-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm">Start Date</Label>
                    <Input
                      type="date"
                      value={eventForm.start_date}
                      onChange={(e) => setEventForm({ ...eventForm, start_date: e.target.value })}
                      required
                      className="mt-1"
                      data-testid="start-date-input"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">End Date</Label>
                    <Input
                      type="date"
                      value={eventForm.end_date}
                      onChange={(e) => setEventForm({ ...eventForm, end_date: e.target.value })}
                      required
                      className="mt-1"
                      data-testid="end-date-input"
                    />
                  </div>
                </div>
                
                <div className="border-t pt-3">
                  <Label className="text-sm font-semibold">Prizes</Label>
                  <div className="space-y-2 mt-2">
                    {eventForm.prizes.map((prize, idx) => (
                      <div key={idx} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="font-outfit font-medium text-sm mb-2">Prize {idx + 1}</p>
                        <div className="space-y-2">
                          <Input
                            type="number"
                            placeholder="Prize Amount ($)"
                            value={prize.prize_amount}
                            onChange={(e) => {
                              const newPrizes = [...eventForm.prizes];
                              newPrizes[idx].prize_amount = e.target.value;
                              setEventForm({ ...eventForm, prizes: newPrizes });
                            }}
                            required
                            className="text-sm"
                            data-testid={`prize-${idx}-amount`}
                          />
                          <Input
                            placeholder="Prize Description"
                            value={prize.prize_description}
                            onChange={(e) => {
                              const newPrizes = [...eventForm.prizes];
                              newPrizes[idx].prize_description = e.target.value;
                              setEventForm({ ...eventForm, prizes: newPrizes });
                            }}
                            required
                            className="text-sm"
                            data-testid={`prize-${idx}-desc`}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <Button type="submit" className="w-full bg-primary hover:bg-primary/90 mt-4" data-testid="submit-event-button">
                  Create Event
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          ) : null}
        </div>

        {/* Active Events Tab */}
        <TabsContent value="active">
          <div className="space-y-3 sm:space-y-4">
            {(events || []).filter(e => e.status === 'active').length === 0 ? (
              <Card className="p-8 sm:p-12 text-center">
                <p className="font-outfit text-sm sm:text-base text-gray-600">No active events</p>
              </Card>
            ) : (
              (events || []).filter(e => e.status === 'active').map((event) => (
                <Card 
                  key={event.event_id} 
                  className="p-4 sm:p-6 hover:shadow-lg transition-shadow cursor-pointer" 
                  data-testid={`event-${event.event_id}`}
                  onClick={() => navigate(`/business/events/${event.event_id}`)}
                >
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                        <h3 className="font-playfair font-bold text-xl sm:text-2xl text-primary break-words">{event.event_name}</h3>
                        <span className="px-3 py-1 rounded-full text-xs sm:text-sm font-outfit bg-green-100 text-green-800 self-start">
                          {event.status}
                        </span>
                      </div>
                      <p className="font-outfit text-sm sm:text-base text-gray-600 mb-3 sm:mb-4 line-clamp-3">{event.event_description}</p>
                      <div className="flex flex-col sm:flex-row gap-3 sm:gap-6 text-xs sm:text-sm mb-3">
                        <div className="flex items-center">
                          <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-amber-600 mr-2 flex-shrink-0" />
                          <span className="font-outfit break-words">{formatDateDisplay(event.start_date)} - {formatDateDisplay(event.end_date)}</span>
                        </div>
                        <div className="flex items-center">
                          <Award className="w-3 h-3 sm:w-4 sm:h-4 text-amber-600 mr-2 flex-shrink-0" />
                          <span className="font-outfit">{(event.prizes || []).length} prizes</span>
                        </div>
                      </div>
                      
                      {/* Prize Details */}
                      {event.prizes && event.prizes.length > 0 && (
                        <div className="mt-3 p-3 bg-amber-50 rounded border border-amber-200">
                          <p className="font-outfit font-semibold text-xs sm:text-sm text-gray-700 mb-2">Prizes:</p>
                          <ul className="space-y-1">
                            {event.prizes.map((prize, idx) => (
                              <li key={idx} className="flex items-start gap-2 text-xs sm:text-sm">
                                <span className="text-amber-600 mt-0.5 flex-shrink-0">•</span>
                                <div className="flex-1 min-w-0">
                                  <span className="font-semibold text-gray-800 break-words">{prize.prize_name || `Prize ${idx + 1}`}</span>
                                  {prize.prize_description && (
                                    <span className="text-gray-600 break-words"> - {prize.prize_description}</span>
                                  )}
                                  <span className="text-amber-600 font-semibold ml-2">${prize.prize_amount}</span>
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-row sm:flex-col gap-2 justify-end sm:justify-start">
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleShowQRPrintout(event);
                        }}
                        size="sm"
                        className="flex-1 sm:flex-initial text-xs sm:text-sm bg-indigo-600 hover:bg-indigo-700"
                        data-testid={`qr-event-${event.event_id}`}
                      >
                        <QrCode className="w-4 h-4 sm:mr-2" />
                        <span className="hidden sm:inline">QR Printout</span>
                      </Button>
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditEvent(event);
                        }}
                        size="sm"
                        variant="outline"
                        className="flex-1 sm:flex-initial text-xs sm:text-sm"
                        data-testid={`edit-event-${event.event_id}`}
                      >
                        Edit Event
                      </Button>
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/business/events/${event.event_id}`);
                        }}
                        size="sm"
                        className="bg-amber-600 hover:bg-amber-700 flex-1 sm:flex-initial text-xs sm:text-sm"
                        data-testid={`view-details-${event.event_id}`}
                      >
                        View Details
                      </Button>
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* Pending Events Tab */}
        <TabsContent value="pending">
          <div className="space-y-4">
            {(events || []).filter(e => e.approval_status === 'pending').length === 0 ? (
              <Card className="p-12 text-center">
                <p className="font-outfit text-gray-600">No pending events</p>
              </Card>
            ) : (
              (events || []).filter(e => e.approval_status === 'pending').map((event) => (
                <Card 
                  key={event.event_id} 
                  className="p-4 sm:p-6 hover:shadow-lg transition-shadow" 
                  data-testid={`pending-event-${event.event_id}`}
                >
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                        <h3 className="font-playfair font-bold text-xl sm:text-2xl text-primary break-words">{event.event_name}</h3>
                        <span className="px-3 py-1 rounded-full text-xs sm:text-sm font-outfit bg-yellow-100 text-yellow-800 self-start">
                          Pending Approval
                        </span>
                      </div>
                      <p className="font-outfit text-sm sm:text-base text-gray-600 mb-3 sm:mb-4 line-clamp-3">{event.event_description}</p>
                      <div className="flex flex-col sm:flex-row gap-3 sm:gap-6 text-xs sm:text-sm mb-3">
                        <div className="flex items-center">
                          <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-amber-600 mr-2 flex-shrink-0" />
                          <span className="font-outfit break-words">{formatDateDisplay(event.start_date)} - {formatDateDisplay(event.end_date)}</span>
                        </div>
                        <div className="flex items-center">
                          <Award className="w-3 h-3 sm:w-4 sm:h-4 text-amber-600 mr-2 flex-shrink-0" />
                          <span className="font-outfit">{(event.prizes || []).length} prizes</span>
                        </div>
                      </div>
                      
                      {/* Prize Details */}
                      {event.prizes && event.prizes.length > 0 && (
                        <div className="mt-3 p-3 bg-amber-50 rounded border border-amber-200">
                          <p className="font-outfit font-semibold text-xs sm:text-sm text-gray-700 mb-2">Prizes:</p>
                          <ul className="space-y-1">
                            {event.prizes.map((prize, idx) => (
                              <li key={idx} className="flex items-start gap-2 text-xs sm:text-sm">
                                <span className="text-amber-600 mt-0.5 flex-shrink-0">•</span>
                                <div className="flex-1 min-w-0">
                                  <span className="font-semibold text-gray-800 break-words">{prize.prize_name || `Prize ${idx + 1}`}</span>
                                  {prize.prize_description && (
                                    <span className="text-gray-600 break-words"> - {prize.prize_description}</span>
                                  )}
                                  <span className="text-amber-600 font-semibold ml-2">${prize.prize_amount}</span>
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      <div className="mt-3 text-xs sm:text-sm text-yellow-700 bg-yellow-50 p-3 rounded">
                        ⏳ This event is awaiting admin approval before it can be activated
                      </div>
                    </div>
                    <div className="flex flex-row sm:flex-col gap-2 justify-end sm:justify-start">
                      <Button
                        onClick={() => handleEditEvent(event)}
                        size="sm"
                        variant="outline"
                        className="flex-1 sm:flex-initial text-xs sm:text-sm"
                        data-testid={`edit-pending-${event.event_id}`}
                      >
                        Edit Event
                      </Button>
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* Completed Events Tab */}
        <TabsContent value="completed">
          <div className="space-y-4">
            {(events || []).filter(e => e.status === 'completed').length === 0 ? (
              <Card className="p-12 text-center">
                <p className="font-outfit text-gray-600">No completed events</p>
              </Card>
            ) : (
              (events || []).filter(e => e.status === 'completed').map((event) => (
                <Card 
                  key={event.event_id} 
                  className="p-6 hover:shadow-lg transition-shadow cursor-pointer" 
                  data-testid={`event-${event.event_id}`}
                  onClick={() => navigate(`/business/events/${event.event_id}`)}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-playfair font-bold text-2xl text-primary">{event.event_name}</h3>
                        <span className="px-3 py-1 rounded-full text-sm font-outfit bg-gray-100 text-gray-800">
                          {event.status}
                        </span>
                      </div>
                      <p className="font-outfit text-gray-600 mb-4">{event.event_description}</p>
                      <div className="flex gap-6 text-sm mb-3">
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 text-amber-600 mr-2" />
                          <span className="font-outfit">{formatDateDisplay(event.start_date)} - {formatDateDisplay(event.end_date)}</span>
                        </div>
                        <div className="flex items-center">
                          <Award className="w-4 h-4 text-amber-600 mr-2" />
                          <span className="font-outfit">{(event.prizes || []).length} prizes</span>
                        </div>
                      </div>
                      
                      {/* Prize Details */}
                      {event.prizes && event.prizes.length > 0 && (
                        <div className="mt-3 p-3 bg-amber-50 rounded border border-amber-200">
                          <p className="font-outfit font-semibold text-sm text-gray-700 mb-2">Prizes:</p>
                          <ul className="space-y-1">
                            {event.prizes.map((prize, idx) => (
                              <li key={idx} className="flex items-start gap-2 text-sm">
                                <span className="text-amber-600 mt-0.5">•</span>
                                <div className="flex-1">
                                  <span className="font-semibold text-gray-800">{prize.prize_name || `Prize ${idx + 1}`}</span>
                                  {prize.prize_description && (
                                    <span className="text-gray-600"> - {prize.prize_description}</span>
                                  )}
                                  <span className="text-amber-600 font-semibold ml-2">${prize.prize_amount}</span>
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/business/events/${event.event_id}`);
                        }}
                        size="sm"
                        className="bg-primary hover:bg-primary/90"
                        data-testid={`view-details-${event.event_id}`}
                      >
                        View Details
                      </Button>
                      {businessInfo?.allow_receipt_deletion && (
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteReceiptFiles(event.event_id);
                          }}
                          size="sm"
                          variant="outline"
                          className="border-red-300 text-red-600 hover:bg-red-50"
                          data-testid={`delete-receipts-${event.event_id}`}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Delete Receipts
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* Edit Event Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent 
            className="max-w-xl max-h-[85vh] overflow-y-auto p-4 sm:p-6" 
            data-testid="edit-event-dialog"
            onInteractOutside={(e) => e.preventDefault()}
          >
            <DialogHeader className="pb-3">
              <DialogTitle className="font-playfair text-xl sm:text-2xl">
                Edit Event
              </DialogTitle>
              {editingEvent?.status === 'active' && (
                <p className="text-xs sm:text-sm text-amber-600 font-outfit mt-1">
                  Note: You can edit name, description, prizes, and dates for active events
                </p>
              )}
            </DialogHeader>
            <form onSubmit={handleUpdateEvent} className="space-y-3 mt-2">
              <div>
                <Label className="text-sm">Event Name</Label>
                <Input
                  value={eventForm.event_name}
                  onChange={(e) => setEventForm({ ...eventForm, event_name: e.target.value })}
                  required
                  className="mt-1"
                  data-testid="edit-event-name-input"
                />
              </div>
              
              <div>
                <Label className="text-sm">Description</Label>
                <Textarea
                  value={eventForm.event_description}
                  onChange={(e) => setEventForm({ ...eventForm, event_description: e.target.value })}
                  required
                  rows={2}
                  className="mt-1"
                  data-testid="edit-event-desc-input"
                />
              </div>

              {editingEvent?.status === 'draft' && (
                <div>
                  <Label className="text-sm">Incentive Points Value</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={eventForm.coupon_value}
                    onChange={(e) => setEventForm({ ...eventForm, coupon_value: e.target.value })}
                    required
                    className="mt-1"
                    data-testid="edit-coupon-value-input"
                  />
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm">Start Date</Label>
                  <Input
                    type="date"
                    value={eventForm.start_date}
                    onChange={(e) => setEventForm({ ...eventForm, start_date: e.target.value })}
                    required
                    className="mt-1"
                    data-testid="edit-start-date-input"
                  />
                </div>
                <div>
                  <Label className="text-sm">End Date</Label>
                  <Input
                    type="date"
                    value={eventForm.end_date}
                    onChange={(e) => setEventForm({ ...eventForm, end_date: e.target.value })}
                    required
                    className="mt-1"
                    data-testid="edit-end-date-input"
                  />
                </div>
              </div>
              
              <div className="border-t pt-3">
                <Label className="text-sm font-semibold">Prizes</Label>
                <div className="space-y-2 mt-2">
                  {(eventForm.prizes || []).map((prize, idx) => (
                    <div key={idx} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="font-outfit font-medium text-sm mb-2">Prize {idx + 1}</p>
                      <div className="space-y-2">
                        <Input
                          type="number"
                          placeholder="Prize Amount ($)"
                          value={prize.prize_amount}
                          onChange={(e) => {
                            const newPrizes = [...eventForm.prizes];
                            newPrizes[idx].prize_amount = e.target.value;
                            setEventForm({ ...eventForm, prizes: newPrizes });
                          }}
                          required
                          className="text-sm"
                          data-testid={`edit-prize-${idx}-amount`}
                        />
                        <Input
                          placeholder="Prize Description"
                          value={prize.prize_description}
                          onChange={(e) => {
                            const newPrizes = [...eventForm.prizes];
                            newPrizes[idx].prize_description = e.target.value;
                            setEventForm({ ...eventForm, prizes: newPrizes });
                          }}
                          required
                          className="text-sm"
                          data-testid={`edit-prize-${idx}-desc`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <Button type="submit" className="w-full bg-primary hover:bg-primary/90 mt-4" data-testid="submit-update-button">
                Update Event
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </Tabs>

      {/* Event QR Printout Modal */}
      {showQRPrintout && selectedEventForQR && (
        <EventQRPrintout 
          event={selectedEventForQR} 
          onClose={handleCloseQRPrintout}
        />
      )}

      {/* Business Settings Dialog */}
      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-playfair text-2xl">Business Settings</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Premium Business Badge */}
            {businessSettings.is_premium && (
              <div className="p-4 bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-300 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Award className="w-5 h-5 text-amber-600" />
                  <p className="text-base font-bold text-amber-800">Premium Business</p>
                </div>
                <p className="text-sm text-amber-700">You have access to premium features including customer account approval</p>
              </div>
            )}

            {/* Auto Approve Customers Toggle */}
            <div className="border rounded-lg p-5 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <UserCheck className="w-5 h-5 text-green-600" />
                    <h4 className="font-outfit font-semibold text-lg">
                      Auto-Approve Customer Join Requests
                    </h4>
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Automatically approve customers when they request to join your business. 
                    When enabled, customers will be instantly approved without requiring manual approval.
                  </p>
                </div>
                <button
                  onClick={() => updateBusinessSettings('auto_approve_customers', !businessSettings.auto_approve_customers)}
                  className={`
                    relative inline-flex h-8 w-14 items-center rounded-full transition-colors flex-shrink-0
                    ${businessSettings.auto_approve_customers ? 'bg-green-600' : 'bg-gray-300'}
                  `}
                  aria-label="Toggle auto-approve customers"
                >
                  <span
                    className={`
                      inline-block h-6 w-6 transform rounded-full bg-white transition-transform shadow-md
                      ${businessSettings.auto_approve_customers ? 'translate-x-7' : 'translate-x-1'}
                    `}
                  />
                </button>
              </div>
              <div className={`
                px-3 py-2 rounded-md text-sm font-medium
                ${businessSettings.auto_approve_customers 
                  ? 'bg-green-50 text-green-700 border border-green-200' 
                  : 'bg-gray-50 text-gray-700 border border-gray-200'}
              `}>
                {businessSettings.auto_approve_customers 
                  ? '✅ Currently ON - Customers are auto-approved instantly'
                  : '⏸️ Currently OFF - Manual approval required for each customer'}
              </div>
            </div>

            {/* Hide Incentive Points Toggle */}
            <div className="border rounded-lg p-5 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Award className="w-5 h-5 text-blue-600" />
                    <h4 className="font-outfit font-semibold text-lg">
                      Hide Incentive Points from Customers
                    </h4>
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Hide the incentive points value from customers viewing your events. 
                    When enabled, customers won't see how much each point is worth until they win.
                    This creates more excitement and surprise for your customers.
                  </p>
                </div>
                <button
                  onClick={() => updateBusinessSettings('hide_coupon_value', !businessSettings.hide_coupon_value)}
                  className={`
                    relative inline-flex h-8 w-14 items-center rounded-full transition-colors flex-shrink-0
                    ${businessSettings.hide_coupon_value ? 'bg-blue-600' : 'bg-gray-300'}
                  `}
                  aria-label="Toggle hide incentive points"
                >
                  <span
                    className={`
                      inline-block h-6 w-6 transform rounded-full bg-white transition-transform shadow-md
                      ${businessSettings.hide_coupon_value ? 'translate-x-7' : 'translate-x-1'}
                    `}
                  />
                </button>
              </div>
              <div className={`
                px-3 py-2 rounded-md text-sm font-medium
                ${businessSettings.hide_coupon_value
                  ? 'bg-blue-50 text-blue-700 border border-blue-200'
                  : 'bg-gray-50 text-gray-700 border border-gray-200'}
              `}>
                {businessSettings.hide_coupon_value
                  ? '🔒 Currently HIDDEN - Customers cannot see incentive points values'
                  : '👁️ Currently VISIBLE - Customers can see incentive points values'}
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>💡 Tip:</strong> These settings help you customize how customers interact with your business. 
                Changes take effect immediately and apply to all current and future events.
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-4 pt-4 border-t">
            <Button 
              onClick={() => setShowSettingsDialog(false)}
              variant="outline"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}